import React, { useEffect, useState, useContext, useMemo } from 'react'
import { Delete, PeopleAltOutlined } from '@mui/icons-material'
import { TableCell, TableRow, Checkbox, Tooltip, Fab, CircularProgress } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { motion } from 'framer-motion'
import { Users as HeaderIcon } from 'lucide-react'
import { useDispatch } from 'react-redux'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { CvsDrawer, Paper } from '@components'
import { AdminHeader } from '@components/layout/admin-header'
import { groupContext, modalContext, userContext } from '@contexts'
import { colors } from '@global/constants'
import { useAppSelector } from '@redux/hooks'
import { api } from 'api'
import { showSnackbar } from 'functions'
import { EditGroup } from './edit-group'
import { CvsTable } from '../../../components/table/cvs-table'
import { EnhancedTableToolbar } from '../../../components/table/enhanced-table-toolbar'
import { Fuzzy } from '../../../utils/fuse-search'
import { t } from '@lingui/core/macro'

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
    height: '100%',
  },
  paper: {
    width: '100%',
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 0,
  },
  table: {
    width: '100%',
    tableLayout: 'fixed',
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  textField: {
    width: 200,
  },
}))

export const GroupList = () => {
  const classes = useStyles()
  const [DATASETS_ENABLED = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.DATASETS_PAGE]),
  )
  const { byId, fetchGroups, updateGroups } = useContext(groupContext)
  const ModalContext = useContext(modalContext)
  const UserContext = useContext(userContext)
  const [loaded, setLoaded] = useState(false)
  const [selected, setSelected] = useState({})
  const [visible, setVisible] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [search, setSearch] = useState('')
  const dispatch = useDispatch()
  const columns = [
    { label: t`Name`, width: '35%', lookup: 'name' },
    { label: t`User Count`, width: '30%', lookup: 'user_count' },
    { label: t`Description`, width: '35%', lookup: 'notes' },
  ]

  const groups = useMemo(() => Object.keys(byId)?.map(id => byId[id]), [byId])
  const [displayGroups, setDisplayGroups] = useState(groups)

  useEffect(() => {
    setDisplayGroups(groups)
  }, [groups])

  useEffect(() => {
    setLoaded(false)
    fetchGroups().then(() => setLoaded(true))
    UserContext.fetchUsers()
  }, [fetchGroups])

  const filterChanged = search => {
    setSearch(search)

    if (!search) {
      setDisplayGroups(groups)
    } else {
      setDisplayGroups(fuzzySearch(search))
    }
  }

  const fuzzySearch = search => {
    const fuzzy = new Fuzzy(groups, { keys: ['name'] })
    return fuzzy.search(search)
  }

  useEffect(() => {
    if (selectedGroup) {
      setVisible(true)
    } else {
      setVisible(false)
    }
  }, [selectedGroup])

  const onReset = () => {}

  const addItem = () => {
    setVisible(true)
  }

  const deleteItems = payload => {
    api
      .post('/api/v1/account/groups/bulk/delete', {
        group_ids: Object.keys(payload).filter(p => payload[p]),
      })
      .then(() => {
        const filteredGroups = groups.filter(g => !payload[g.id])
        updateGroups(filteredGroups)
        setSelected({})
        dispatch(
          showSnackbar({
            message: t`Group deleted successfully`,
            type: 'SUCCESS',
            duration: 5000,
          }),
        )
      })
      .catch(() => {
        dispatch(
          showSnackbar({
            message: t`Group failed to delete`,
            type: 'ERROR',
            duration: 5000,
          }),
        )
      })
  }

  const deleteItemsModal = () => {
    ModalContext.displayModal({
      title: t`Are you sure you want to delete these groups`,
      description: t`This action can't be undone.`,
      confirm: () => {
        return () => {
          deleteItems(selected)
        }
      },
    })
  }

  const numSelected = Object.values(selected).filter(val => !!val).length
  const entity = DATASETS_ENABLED ? 'datasets' : 'templates'
  const groupName = (selectedGroup && byId[selectedGroup]?.name) || 'New Group'

  return (
    <motion.div
      className={classes.root}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      style={{ overflow: 'hidden' }}
    >
      <AdminHeader>
        <AdminHeader.PageIcon>
          <HeaderIcon color="hsla(194, 47%, 20%, 1)" size={28} />
        </AdminHeader.PageIcon>
        <div className="ml-4">
          <AdminHeader.Title>{t`Groups`}</AdminHeader.Title>
          <AdminHeader.Subtitle>
            {t`A convenient way to organize Groups and share ${entity}`}
          </AdminHeader.Subtitle>
        </div>
      </AdminHeader>
      <Paper elevation={2} className={classes.paper}>
        {loaded ? (
          <div className={classes.table} style={{ height: 'calc(100% - 145px)' }}>
            <EnhancedTableToolbar
              numSelected={numSelected}
              filterChanged={filterChanged}
              addItem={addItem}
              term={search}
              deleteItems={deleteItems}
              noAdd={false}
              onReset={onReset}
              blockMultipleEdit={false}
            >
              <Tooltip
                title={t`Delete`}
                style={{ backgroundColor: colors.red.light, color: 'white' }}
              >
                <Fab
                  color="inherit"
                  size="small"
                  aria-label="Add"
                  className={classes.fab}
                  onClick={deleteItemsModal}
                >
                  <Delete />
                </Fab>
              </Tooltip>
            </EnhancedTableToolbar>
            <div style={{ height: 'calc(100% - 64px)' }}>
              <CvsTable
                columns={columns}
                rows={displayGroups}
                orderBy="name"
                sortOrder="asc"
                keyField="id"
                noAdd={false}
                addItem={addItem}
                noPagination={true}
                style={{ maxWidth: '100%' }}
                multiSelect={true}
                emptyStateMessage={{
                  icon: (
                    <Fab
                      style={{
                        height: '65px',
                        width: '65px',
                        color: colors.gray.light,
                        cursor: 'auto',
                      }}
                      disabled
                    >
                      <PeopleAltOutlined style={{ fontSize: '34px', color: colors.blue.smokey }} />
                    </Fab>
                  ),
                  title: t`No Groups Found.`,
                  description: t`Try a different search or add a new group.`,
                }}
                selectAll={val => {
                  setSelected(
                    displayGroups.reduce(
                      (memo, item) => ({
                        ...memo,
                        [item.id]: val,
                      }),
                      {},
                    ),
                  )
                }}
              >
                {({ row }) => {
                  return (
                    <TableRow
                      hover
                      onClick={() => {
                        numSelected
                          ? setSelected({
                              ...selected,
                              [row.id]: !selected[row.id],
                            })
                          : setSelectedGroup(row.id)
                      }}
                      tabIndex={-1}
                      key={row.id}
                      style={{ cursor: 'pointer', maxWidth: '100%', overflow: 'hidden' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          onClick={e => {
                            e.stopPropagation()
                            setSelected({
                              ...selected,
                              [row.id]: !selected[row.id],
                            })
                          }}
                          checked={selected[row.id]}
                        />
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.name}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.user_count}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.notes}
                      </TableCell>
                    </TableRow>
                  )
                }}
              </CvsTable>
            </div>
          </div>
        ) : (
          <div style={{ padding: 24, display: 'flex', width: '100%', justifyContent: 'center' }}>
            <CircularProgress color="primary" />
          </div>
        )}
      </Paper>
      <CvsDrawer
        visible={Boolean(visible)}
        onClose={() => {
          setSelectedGroup(null)
          setVisible(false)
        }}
        title={t`Edit ${groupName}`}
      >
        <EditGroup
          groupId={selectedGroup}
          onClose={() => {
            setSelectedGroup(null)
            setVisible(false)
          }}
        />
      </CvsDrawer>
    </motion.div>
  )
}
